import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { ArrowBack, Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { ContentState, EditorState, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { Formik } from 'formik';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { ActivityFormV2 } from '../../../program-manager/activity-management/activity-form-v2/ActivityFormV2';
import { RosterTemplateForm } from '../../../program-manager/rosters/roster-templates-screen/RosterTemplateForm';
import { AutoLocationField } from '../../../../common/auto-location-field/AutoLocationField';
import { EntityType } from './form-responses-types';
import { useSendCalendarInvite } from './send-calendar-invite-api';
import { useSendFormSubmissionEmail } from './send-form-submission-email-api';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useUser } from '../../../../global/auth/useUser';
import { GET_ORG_COMM_PREFERENCES } from '../../admin-console/Communications/Preferences/admin-console-preferences-model.gql';
import { GetOrgCommPreferences } from '../../admin-console/Communications/Preferences/__generated__/GetOrgCommPreferences';

const splitEmailList = (value: string): string[] =>
  value
    .split(/[,;]/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

interface FormWizardDialogProps {
  open: boolean;
  wizardStep: number;
  selectedEntityType: EntityType | null;
  activityPrefilledValues: any | undefined;
  rosterTemplatePrefilledValues: any | undefined;
  emailPrefilledValues: any | undefined;
  calendarInvitePrefilledValues: any | undefined;
  applicantPrefilledValues?: any | undefined;
  applicantTaskPrefill?: Array<{ taskId: string; taskFieldValues: any[] }>;
  editCalendarInvite?: { formSubmissionCreationId: string; data: any } | null;
  prefillLoading?: boolean;
  submissionId: string | null;
  onClose: () => void;
  onStepChange: (step: number) => void;
  onEntityTypeChange: (type: EntityType | null) => void;
  onEntityCreated: (entityType: EntityType, entityId: string) => void;
  onSuccess: () => void;
}

export const FormWizardDialog: React.FC<FormWizardDialogProps> = ({
  open,
  wizardStep,
  selectedEntityType,
  activityPrefilledValues,
  rosterTemplatePrefilledValues,
  emailPrefilledValues,
  calendarInvitePrefilledValues,
  applicantPrefilledValues,
  applicantTaskPrefill,
  editCalendarInvite,
  prefillLoading,
  submissionId,
  onClose,
  onStepChange,
  onEntityTypeChange,
  onEntityCreated,
  onSuccess,
}) => {
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const { data: orgCommPreferenceData } = useCampfireQuery<GetOrgCommPreferences, undefined>(GET_ORG_COMM_PREFERENCES);
  const defaultReplyToEmail = orgCommPreferenceData?.tm.tenant.config?.communication?.replyTo || 'noreply@volaby.org';
  const { send: sendCalendarInvite } = useSendCalendarInvite();
  const { send: sendSubmissionEmail } = useSendFormSubmissionEmail();
  const apiUrl = useApiUrl();
  const addApplicantFetch = useCampfireFetch({ defer: true });
  const saveCompletedTaskFetch = useCampfireFetch({ defer: true });

  const htmlToEditorState = (html: string): EditorState => {
    if (!html) return EditorState.createEmpty();
    const cleaned = html.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
    const blocksFromHTML = convertFromHTML(cleaned);
    if (blocksFromHTML.contentBlocks) {
      const content = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      return EditorState.createWithContent(content);
    }
    return EditorState.createEmpty();
  };

  const isEditingInvite = !!editCalendarInvite?.data;

  const recipientEmail: string | undefined = emailPrefilledValues?.recipientEmail;

  const buildCalendarInviteLocation = (loc: string | null) => ({
    description: loc || '',
    formatted: loc || '',
    latitude: 0,
    longitude: 0,
    placesId: '',
  });

  const buildCalendarInitialValues = () => {
    const data = editCalendarInvite?.data;
    if (data) {
      const start = data.startDateTime as string | null;
      const end = data.endDateTime as string | null;
      const meetingType: 'in_person' | 'online' | 'phone' = data.meetingLink
        ? 'online'
        : data.phoneNumber
        ? 'phone'
        : 'in_person';
      return {
        attendeeEmails: Array.isArray(data.attendeeEmails) ? data.attendeeEmails : ([] as string[]),
        emailInput: '',
        title: data.title || '',
        date: start ? start.substring(0, 10) : '',
        startTime: start ? start.substring(11, 16) : '',
        endTime: end ? end.substring(11, 16) : '',
        meetingType,
        location: buildCalendarInviteLocation(data.location ?? null),
        meetingLink: data.meetingLink || '',
        phoneNumber: data.phoneNumber || '',
        notes: htmlToEditorState(data.description || ''),
      };
    }
    return {
      attendeeEmails: calendarInvitePrefilledValues?.attendeeEmail
        ? [calendarInvitePrefilledValues.attendeeEmail]
        : ([] as string[]),
      emailInput: '',
      title: calendarInvitePrefilledValues?.title || '',
      date: calendarInvitePrefilledValues?.date || '',
      startTime: calendarInvitePrefilledValues?.startTime || '',
      endTime: calendarInvitePrefilledValues?.endTime || '',
      meetingType: (calendarInvitePrefilledValues?.meetingType as 'in_person' | 'online' | 'phone') || 'in_person',
      location: calendarInvitePrefilledValues?.location || buildCalendarInviteLocation(null),
      meetingLink: calendarInvitePrefilledValues?.meetingLink || '',
      phoneNumber: calendarInvitePrefilledValues?.phoneNumber || '',
      notes: htmlToEditorState(calendarInvitePrefilledValues?.notes || ''),
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={wizardStep === 1 ? 'lg' : 'sm'}
      fullWidth
      fullScreen={wizardStep === 1}
    >
      {wizardStep === 0 ? (
        <DialogTitle style={{ paddingBottom: 0 }}>
          <Grid container justify='space-between'>
            <Grid item>
              <Typography
                style={{
                  fontSize: '23px',
                  fontWeight: 550,
                  color: theme.color.grey.neutral400,
                  paddingLeft: '5px',
                }}
              >
                Form Wizard
              </Typography>
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={onClose}>
                <Close color='action' />
              </IconButton>
            </Grid>
          </Grid>
          <Box pr={3} mt={1}>
            <Typography style={{ fontSize: 14, paddingLeft: '5px', color: theme.color.grey.neutral300 }}>
              Create an entity from this form submission. Select what you would like to create.
            </Typography>
          </Box>
        </DialogTitle>
      ) : (
        <DialogTitle style={{ padding: '8px 16px', borderBottom: `1px solid ${theme.color.grey.neutralBrand200}` }}>
          <Grid container justify='space-between' alignItems='center'>
            <Grid item>
              <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                <TabletButton
                  variant='text'
                  size='small'
                  onClick={() => onStepChange(0)}
                  style={{ minWidth: 'auto', padding: '4px 8px' }}
                >
                  <ArrowBack style={{ fontSize: '18px' }} />
                </TabletButton>
                <Typography
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.color.grey.neutral400,
                  }}
                >
                  Create{' '}
                  {selectedEntityType === 'activity'
                    ? 'Activity'
                    : selectedEntityType === 'roster_template'
                    ? 'Roster Template'
                    : selectedEntityType === 'email'
                    ? 'Email'
                    : 'Calendar Invite'}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={onClose}>
                <Close color='action' />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
      )}
      <DialogContent>
        {wizardStep === 0 && (
          <Box py={2}>
            <Box display='flex' flexDirection='column' style={{ gap: '12px' }}>
              <Box
                onClick={() => onEntityTypeChange('activity')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'activity' ? theme.palette.primary.main : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'activity' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'activity'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z'
                        fill={selectedEntityType === 'activity' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Activity
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a new volunteer activity from this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('roster_template')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'roster_template'
                      ? theme.palette.primary.main
                      : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'roster_template' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'roster_template'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 3H14.82C14.4 1.84 13.3 1 12 1C10.7 1 9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 3C12.55 3 13 3.45 13 4C13 4.55 12.55 5 12 5C11.45 5 11 4.55 11 4C11 3.45 11.45 3 12 3ZM14 17H7V15H14V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z'
                        fill={selectedEntityType === 'roster_template' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Roster Template
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a roster template for scheduling volunteers
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('email')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'email' ? theme.palette.primary.main : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'email' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'email' ? theme.palette.primary.main : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z'
                        fill={selectedEntityType === 'email' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Email
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Send an email based on this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('calendar_invite')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'calendar_invite'
                      ? theme.palette.primary.main
                      : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'calendar_invite' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'calendar_invite'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z'
                        fill={selectedEntityType === 'calendar_invite' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Calendar Invite
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a calendar event from this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('applicant')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'applicant' ? theme.palette.primary.main : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'applicant' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'applicant'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M15 12C17.21 12 19 10.21 19 8C19 5.79 17.21 4 15 4C12.79 4 11 5.79 11 8C11 10.21 12.79 12 15 12ZM6 10V7H4V10H1V12H4V15H6V12H9V10H6ZM15 14C12.33 14 7 15.34 7 18V20H23V18C23 15.34 17.67 14 15 14Z'
                        fill={selectedEntityType === 'applicant' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Applicant
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create an applicant and start their onboarding from this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {wizardStep === 1 && prefillLoading && (
          <Box display='flex' alignItems='center' justifyContent='center' style={{ height: 'calc(100vh - 200px)' }}>
            <CircularProgress />
          </Box>
        )}
        {wizardStep === 1 && !prefillLoading && selectedEntityType === 'activity' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <ActivityFormV2
              onSuccess={(activityId) => {
                onEntityCreated('activity', activityId);
                onSuccess();
              }}
              predefinedFormValues={activityPrefilledValues}
            />
          </Box>
        )}
        {wizardStep === 1 && !prefillLoading && selectedEntityType === 'roster_template' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <RosterTemplateForm
              onSuccess={() => {
                onSuccess();
              }}
              predefinedFormValues={rosterTemplatePrefilledValues}
              enableFlexibleSessions
              onCreated={(creationResult) => {
                if (creationResult.rosterTemplateIds.length > 0) {
                  onEntityCreated('roster_template', creationResult.rosterTemplateIds[0]);
                }
              }}
            />
          </Box>
        )}
        {wizardStep === 1 && !prefillLoading && selectedEntityType === 'email' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto', padding: '24px' }}>
            <Formik
              initialValues={{
                subject: emailPrefilledValues?.subject || '',
                replyTo: emailPrefilledValues?.replyTo ? 'template' : 'default',
                cc: emailPrefilledValues?.cc || '',
                bcc: emailPrefilledValues?.bcc || '',
                message: htmlToEditorState(emailPrefilledValues?.message || ''),
              }}
              onSubmit={async (values) => {
                if (!submissionId || !recipientEmail) return;
                if (!values.subject.trim()) {
                  setSnackbar({ open: true, message: 'Please enter a subject', variant: 'error' });
                  return;
                }
                if (!values.message.getCurrentContent().hasText()) {
                  setSnackbar({ open: true, message: 'Please enter a message', variant: 'error' });
                  return;
                }

                try {
                  const resolveReplyTo = () => {
                    if (values.replyTo === 'current') return user.email;
                    if (values.replyTo === 'template') return emailPrefilledValues?.replyTo || defaultReplyToEmail;
                    return defaultReplyToEmail;
                  };

                  const result = await sendSubmissionEmail({
                    formSubmissionId: submissionId,
                    subject: values.subject,
                    body: stateToHTML(values.message.getCurrentContent()),
                    replyTo: resolveReplyTo(),
                    cc: splitEmailList(values.cc),
                    bcc: splitEmailList(values.bcc),
                  });

                  if (result.ok) {
                    setSnackbar({ open: true, message: 'Email sent successfully', variant: 'success' });
                    onEntityCreated('email', submissionId);
                    onSuccess();
                  } else {
                    const message = (result.data as any)?.message || 'Failed to send email';
                    setSnackbar({ open: true, message, variant: 'error' });
                  }
                } catch (error) {
                  setSnackbar({ open: true, message: 'Failed to send email', variant: 'error' });
                }
              }}
            >
              {(formik) => (
                <Box>
                  <Typography variant='h6' style={{ marginBottom: '24px' }}>
                    Compose Email
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#666' }}>
                        To
                      </Typography>
                      {recipientEmail ? (
                        <Chip label={recipientEmail} size='small' />
                      ) : (
                        <Typography style={{ fontSize: '0.85rem', color: theme.palette.error.main }}>
                          No submitter email available for this submission. Map an Email field under Settings → Form
                          Wizard → Approval Workflow Emails, and make sure the submitter filled it in.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Subject'
                        variant='outlined'
                        value={formik.values.subject}
                        onChange={(e) => formik.setFieldValue('subject', e.target.value)}
                        placeholder='Enter email subject'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant='outlined' fullWidth>
                        <InputLabel>Reply to</InputLabel>
                        <Select
                          value={formik.values.replyTo}
                          onChange={(e) => formik.setFieldValue('replyTo', e.target.value as string)}
                          label='Reply to'
                        >
                          {emailPrefilledValues?.replyTo && (
                            <MenuItem value='template'>Form default ({emailPrefilledValues.replyTo})</MenuItem>
                          )}
                          <MenuItem value='default'>Default Reply ({defaultReplyToEmail})</MenuItem>
                          <MenuItem value='current'>
                            {user.firstName} {user.lastName} ({user.email})
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Cc'
                        variant='outlined'
                        value={formik.values.cc}
                        onChange={(e) => formik.setFieldValue('cc', e.target.value)}
                        placeholder='name@example.org, other@example.org'
                        helperText='Separate multiple addresses with commas'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Bcc'
                        variant='outlined'
                        value={formik.values.bcc}
                        onChange={(e) => formik.setFieldValue('bcc', e.target.value)}
                        placeholder='name@example.org, other@example.org'
                        helperText='Separate multiple addresses with commas'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#666' }}>
                        Message
                      </Typography>
                      <Box style={{ border: '1px solid #ccc', borderRadius: '4px', minHeight: '300px' }}>
                        <CampfireRichEditor
                          editorState={formik.values.message}
                          setEditorState={(state) => formik.setFieldValue('message', state)}
                          placeholder='Compose your message...'
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Box mt={3} display='flex' justifyContent='flex-end' style={{ gap: '12px' }}>
                    <TabletButton variant='text' onClick={onClose}>
                      Cancel
                    </TabletButton>
                    <TabletButton
                      variant='contained'
                      disabled={!recipientEmail || formik.isSubmitting}
                      onClick={() => formik.handleSubmit()}
                    >
                      {formik.isSubmitting ? 'Sending...' : 'Send Email'}
                    </TabletButton>
                  </Box>
                </Box>
              )}
            </Formik>
          </Box>
        )}
        {wizardStep === 1 && !prefillLoading && selectedEntityType === 'applicant' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto', padding: '24px' }}>
            <Formik
              initialValues={{
                email: applicantPrefilledValues?.email || '',
                firstName: applicantPrefilledValues?.firstName || '',
                lastName: applicantPrefilledValues?.lastName || '',
                contactNumber: applicantPrefilledValues?.contactNumber || '',
                dob: applicantPrefilledValues?.dob || '',
                sendInvite: false,
              }}
              onSubmit={(values, { setSubmitting }) => {
                if (!values.email || !values.firstName || !values.lastName) {
                  setSnackbar({
                    open: true,
                    message: 'Email, first name and last name are required',
                    variant: 'error',
                  });
                  setSubmitting(false);
                  return;
                }
                addApplicantFetch
                  .run({
                    url: `${apiUrl}/vm/application/add`,
                    method: 'post',
                    data: {
                      sendInvite: values.sendInvite,
                      applicant: {
                        email: values.email,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        contactNumber: values.contactNumber || undefined,
                        dob: values.dob || undefined,
                      },
                    },
                  })
                  .then(async (res: any) => {
                    const created = res?.data?.data?.importedApplicants?.[0];
                    if (!res.ok || !created) {
                      setSubmitting(false);
                      setSnackbar({
                        open: true,
                        message: 'This person may already exist, or the details were rejected',
                        variant: 'error',
                      });
                      return;
                    }

                    const tasks = applicantTaskPrefill ?? [];
                    let carried = 0;

                    for (let i = 0; i < tasks.length; i += 1) {
                      const task = tasks[i];
                      try {
                        // eslint-disable-next-line no-await-in-loop
                        const taskRes = await saveCompletedTaskFetch.run({
                          url: `${apiUrl}/vm/volunteer/profile/completed-task/save`,
                          method: 'post',
                          data: {
                            userId: created.userId,
                            taskId: task.taskId,
                            taskFieldValues: task.taskFieldValues,
                          },
                        });
                        if (taskRes.ok) carried += 1;
                      } catch (taskError) {
                        // a failed task must not lose the applicant that was just created
                      }
                    }

                    setSubmitting(false);
                    setSnackbar({
                      open: true,
                      message:
                        tasks.length === 0
                          ? 'Applicant created'
                          : `Applicant created, ${carried} of ${tasks.length} tasks pre-filled`,
                      variant: carried < tasks.length ? 'warning' : 'success',
                    });
                    onEntityCreated('applicant', created.userId);
                    onSuccess();
                  })
                  .catch(() => {
                    setSubmitting(false);
                    setSnackbar({ open: true, message: 'Could not create the applicant', variant: 'error' });
                  });
              }}
            >
              {({ values, setFieldValue, submitForm, isSubmitting }) => (
                <Box>
                  <Typography style={{ fontSize: '0.85rem', color: theme.color.grey.neutral300, marginBottom: '20px' }}>
                    Values are taken from the mappings on this form. Check them before creating the record.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        variant='outlined'
                        size='small'
                        label='First name'
                        value={values.firstName}
                        onChange={(e) => setFieldValue('firstName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        variant='outlined'
                        size='small'
                        label='Last name'
                        value={values.lastName}
                        onChange={(e) => setFieldValue('lastName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type='email'
                        variant='outlined'
                        size='small'
                        label='Email'
                        value={values.email}
                        onChange={(e) => setFieldValue('email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        label='Contact number'
                        value={values.contactNumber}
                        onChange={(e) => setFieldValue('contactNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        type='date'
                        label='Date of birth'
                        InputLabelProps={{ shrink: true }}
                        value={values.dob}
                        onChange={(e) => setFieldValue('dob', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color='primary'
                            checked={values.sendInvite}
                            onChange={(e) => setFieldValue('sendInvite', e.target.checked)}
                          />
                        }
                        label='Email an invitation now'
                      />
                      <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                        Leave this off to create the record quietly and invite them later.
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box display='flex' justifyContent='flex-end' style={{ marginTop: '24px' }}>
                    <TabletButton
                      variant='contained'
                      color='primary'
                      disabled={isSubmitting}
                      onClick={() => submitForm()}
                    >
                      {isSubmitting ? 'Creating…' : 'Create Applicant'}
                    </TabletButton>
                  </Box>
                </Box>
              )}
            </Formik>
          </Box>
        )}
        {wizardStep === 1 && !prefillLoading && selectedEntityType === 'calendar_invite' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto', padding: '24px' }}>
            <Formik
              enableReinitialize
              initialValues={buildCalendarInitialValues()}
              onSubmit={async (values) => {
                if (values.attendeeEmails.length === 0 || !values.title || !values.date) {
                  setSnackbar({ open: true, message: 'Please fill in all required fields', variant: 'error' });
                  return;
                }

                const startDateTime = values.startTime ? `${values.date}T${values.startTime}:00` : null;
                const endDateTime = values.endTime ? `${values.date}T${values.endTime}:00` : null;

                const locationText =
                  values.meetingType === 'in_person' ? values.location.description || values.location.formatted : null;

                const result = await sendCalendarInvite({
                  attendeeEmails: values.attendeeEmails,
                  title: values.title,
                  description: values.notes.getCurrentContent().hasText()
                    ? stateToHTML(values.notes.getCurrentContent())
                    : '',
                  startDateTime,
                  endDateTime,
                  location: locationText,
                  meetingLink: values.meetingType === 'online' ? values.meetingLink : null,
                  phoneNumber: values.meetingType === 'phone' ? values.phoneNumber : null,
                  formSubmissionId: submissionId,
                  formSubmissionCreationId: editCalendarInvite?.formSubmissionCreationId ?? null,
                });

                if (result.ok) {
                  setSnackbar({
                    open: true,
                    message: isEditingInvite ? 'Calendar invite updated' : 'Calendar invite sent successfully',
                    variant: 'success',
                  });
                  onSuccess();
                } else {
                  setSnackbar({
                    open: true,
                    message: isEditingInvite ? 'Failed to update calendar invite' : 'Failed to send calendar invite',
                    variant: 'error',
                  });
                }
              }}
            >
              {(formik) => (
                <Box>
                  <Typography variant='h6' style={{ marginBottom: '24px' }}>
                    {isEditingInvite ? 'Update Calendar Event' : 'Create Calendar Event'}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#666' }}>
                        Attendee Emails <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      {formik.values.attendeeEmails.length > 0 && (
                        <Box display='flex' flexWrap='wrap' style={{ gap: '6px', marginBottom: '8px' }}>
                          {formik.values.attendeeEmails.map((email: string) => (
                            <Chip
                              key={email}
                              label={email}
                              size='small'
                              onDelete={() => {
                                formik.setFieldValue(
                                  'attendeeEmails',
                                  formik.values.attendeeEmails.filter((e: string) => e !== email)
                                );
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      <TextField
                        fullWidth
                        variant='outlined'
                        type='email'
                        value={formik.values.emailInput}
                        onChange={(e) => formik.setFieldValue('emailInput', e.target.value)}
                        placeholder={
                          formik.values.attendeeEmails.length === 0 ? 'email@example.com' : 'Add another email...'
                        }
                        helperText='Press Enter or comma to add an email'
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            const raw = formik.values.emailInput.replace(/,/g, '').trim();
                            if (raw && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
                              if (!formik.values.attendeeEmails.includes(raw)) {
                                formik.setFieldValue('attendeeEmails', [...formik.values.attendeeEmails, raw]);
                              }
                              formik.setFieldValue('emailInput', '');
                            }
                          }
                        }}
                        onBlur={() => {
                          const raw = formik.values.emailInput.replace(/,/g, '').trim();
                          if (raw && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
                            if (!formik.values.attendeeEmails.includes(raw)) {
                              formik.setFieldValue('attendeeEmails', [...formik.values.attendeeEmails, raw]);
                            }
                            formik.setFieldValue('emailInput', '');
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Event Title'
                        variant='outlined'
                        value={formik.values.title}
                        onChange={(e) => formik.setFieldValue('title', e.target.value)}
                        placeholder='Enter event title'
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Date'
                        type='date'
                        variant='outlined'
                        value={formik.values.date}
                        onChange={(e) => formik.setFieldValue('date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label='Start Time'
                        type='time'
                        variant='outlined'
                        value={formik.values.startTime}
                        onChange={(e) => formik.setFieldValue('startTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label='End Time'
                        type='time'
                        variant='outlined'
                        value={formik.values.endTime}
                        onChange={(e) => formik.setFieldValue('endTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel>Meeting Type</InputLabel>
                        <Select
                          value={formik.values.meetingType}
                          onChange={(e) =>
                            formik.setFieldValue('meetingType', e.target.value as 'in_person' | 'online' | 'phone')
                          }
                          label='Meeting Type'
                        >
                          <MenuItem value='in_person'>In-Person</MenuItem>
                          <MenuItem value='online'>Online</MenuItem>
                          <MenuItem value='phone'>Phone</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formik.values.meetingType === 'in_person' && (
                      <Grid item xs={12}>
                        <AutoLocationField name='location' label='Location' helper fullWidth variant='outlined' />
                      </Grid>
                    )}
                    {formik.values.meetingType === 'online' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label='Meeting Link'
                          variant='outlined'
                          value={formik.values.meetingLink}
                          onChange={(e) => formik.setFieldValue('meetingLink', e.target.value)}
                          placeholder='https://zoom.us/j/...'
                        />
                      </Grid>
                    )}
                    {formik.values.meetingType === 'phone' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label='Phone Number'
                          variant='outlined'
                          value={formik.values.phoneNumber}
                          onChange={(e) => formik.setFieldValue('phoneNumber', e.target.value)}
                          placeholder='+1 (555) 123-4567'
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#666' }}>
                        Notes (Optional)
                      </Typography>
                      <Box style={{ border: '1px solid #ccc', borderRadius: '4px', minHeight: '150px' }}>
                        <CampfireRichEditor
                          editorState={formik.values.notes}
                          setEditorState={(state) => formik.setFieldValue('notes', state)}
                          placeholder='Add any additional details...'
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Box mt={3} display='flex' justifyContent='flex-end' style={{ gap: '12px' }}>
                    <TabletButton variant='text' onClick={onClose}>
                      Cancel
                    </TabletButton>
                    <TabletButton variant='contained' onClick={() => formik.handleSubmit()}>
                      {isEditingInvite ? 'Update Event' : 'Create Event'}
                    </TabletButton>
                  </Box>
                </Box>
              )}
            </Formik>
          </Box>
        )}
      </DialogContent>
      {wizardStep === 0 && (
        <DialogActions>
          <Box padding={2} display='flex' style={{ gap: '8px' }}>
            <TabletButton variant='text' color='error' onClick={onClose}>
              Cancel
            </TabletButton>
            <TabletButton variant='contained' disabled={!selectedEntityType} onClick={() => onStepChange(1)}>
              Continue
            </TabletButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};
